import React, { Component } from 'react';
import LayoutComponent from '../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../context/CustomContext';
import { FileExcelOutlined } from '@ant-design/icons';
import { FormattedDate, FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Button, Space, Table, Tooltip } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { Task } from '../../../model/entities';
import { Page } from '../../../model/elements';
import tableService from '../../../services/TableService';
import { Link } from 'react-router-dom';
import Search from 'antd/lib/input/Search';
import taskApi from '../../../api/TaskApi';
import notificationService from '../../../services/NotificationService';
import { TaskStatusType, TaskType } from '../../../model/types';
import TaskStatusComponent from '../../../components/TaskStatusComponent/TaskStatusComponent';
import TaskTypeComponent from '../../../components/TaskTypeComponent/TaskTypeComponent';

class TasksPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    constructor(props: Props) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            this.setState({ loading: 'loading' });
            const tasksPage = await taskApi.list(0, tableService.pageSize, 'audit.updated', false);
            this.setState({ tasksPage });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    list = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        const { searchText } = this.state;
        try {
            this.setState({ loading: 'loading' });
            const page = pagination.current!;
            const pageSize = pagination.pageSize!;
            const sortField = sorter.field;
            const sortOrder = sorter.order === 'ascend';
            const tasksPage = await taskApi.list(page - 1, pageSize, sortField, sortOrder, searchText);
            this.setState({ tasksPage });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    search = async (searchText: string) => {
        const { tasksPage } = this.state;
        try {
            this.setState({ loading: 'loading' });
            const pageSize = tasksPage!.size;
            const sortField = tasksPage!.sort.field!;
            const sortOrder = tasksPage!.sort.order!;
            const tasksPageNew = await taskApi.list(0, pageSize, sortField, sortOrder, searchText);
            this.setState({ tasksPage: tasksPageNew, searchText });
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    exportSpreadSheet = async () => {
        const { tasksPage, searchText } = this.state;
        try {
            this.setState({ loading: 'exporting' });
            const sortField = tasksPage!.sort.field!;
            const sortOrder = tasksPage!.sort.order!;
            await taskApi.exportSpreadSheet(sortField, sortOrder, searchText);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: undefined });
        }
    };

    getPath = (task: Task): string => {
        switch (task.taskType) {
            case 'SYNC':
                return 'syncs';
            case 'MEMBER_COMPANY_UPDATE':
            case 'MEMBER_STATUS_UPDATE':
            case 'MEMBER_COST_CENTER_UPDATE':
            case 'MEMBER_IMPORT':
            case 'PROJECT_MEMBER_COMPANY_UPDATE':
            case 'PROJECT_MEMBER_ROLES_UPDATE':
            case 'PROJECT_MEMBER_IMPORT':
            case 'PROJECT_MEMBER_DELETE':
                return 'members';
            case 'PROJECT_ISSUES_COPY':
                return 'project-issues';
            default:
                return '';
        }
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        return (
            <>
                {this.renderToolbar()}
                {this.renderTable()}
            </>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        const { intl } = this.props;
        const { loading } = this.state;
        const placeholder: string = intl.formatMessage({ id: 'tasks.search' });

        return (
            <div className="toolbar">
                <Search placeholder={placeholder} onSearch={this.search} size="large" className="search" />
                <Space>
                    <Tooltip title={<FormattedMessage id="tasks.exportSpreadSheet" />} placement="left">
                        <Button
                            size="large"
                            icon={<FileExcelOutlined />}
                            onClick={this.exportSpreadSheet}
                            loading={loading === 'exporting'}
                        ></Button>
                    </Tooltip>
                </Space>
            </div>
        );
    };

    renderName = (task: Task): React.ReactElement | string | undefined => {
        switch (task.taskType) {
            case 'SYNC':
            case 'PROJECT_ISSUES_COPY':
                return task.name;
            default:
                return <FormattedMessage id={task.taskType} />;
        }
    };

    renderTable = (): React.ReactElement | undefined => {
        const { tasksPage, loading } = this.state;
        const items = tasksPage ? tasksPage.content : [];
        const columns: ColumnsType<Task> = [
            {
                title: '#',
                dataIndex: 'id',
                key: 'id',
                sorter: true,
                align: 'right',
                width: 50,
                render: (value: string, task: Task) => (
                    <Link to={`/tasks/${this.getPath(task)}/${task.id}`}>{value}</Link>
                ),
            },
            {
                title: <FormattedMessage id="task.name" />,
                dataIndex: 'name',
                key: 'name',
                sorter: true,
                render: (value: string, task: Task) => (
                    <Link to={`/tasks/${this.getPath(task)}/${task.id}`}>{this.renderName(task)}</Link>
                ),
            },
            {
                title: <FormattedMessage id="task.audit.creator" />,
                dataIndex: 'audit.creator.firstName',
                key: 'creator',
                width: 250,
                sorter: true,
                render: (value: string, task: Task) => (
                    <Link to={`/tasks/${this.getPath(task)}/${task.id}`}>
                        {`${task.audit?.creator.firstName} ${task.audit?.creator.lastName}`}
                    </Link>
                ),
            },
            {
                title: <FormattedMessage id="task.audit.created" />,
                dataIndex: 'audit.created',
                key: 'created',
                sorter: true,
                width: 220,
                align: 'center',
                render: (value: string, task: Task) => (
                    <Link to={`/tasks/${this.getPath(task)}/${task.id}`}>
                        <FormattedDate
                            value={task.audit!.created as any}
                            day="2-digit"
                            month="2-digit"
                            year="numeric"
                            hour="2-digit"
                            minute="2-digit"
                            second="2-digit"
                        />
                    </Link>
                ),
            },
            {
                title: <FormattedMessage id="task.audit.updated" />,
                dataIndex: 'audit.updated',
                key: 'updated',
                sorter: true,
                defaultSortOrder: 'descend',
                width: 220,
                align: 'center',
                render: (value: string, task: Task) => (
                    <Link to={`/tasks/${this.getPath(task)}/${task.id}`}>
                        <FormattedDate
                            value={task.audit!.updated as any}
                            day="2-digit"
                            month="2-digit"
                            year="numeric"
                            hour="2-digit"
                            minute="2-digit"
                            second="2-digit"
                        />
                    </Link>
                ),
            },
            {
                title: <FormattedMessage id="task.taskType" />,
                dataIndex: 'taskType',
                key: 'taskType',
                sorter: true,
                width: 120,
                align: 'center',
                render: (taskType: TaskType, task: Task) => (
                    <Link to={`/tasks/${this.getPath(task)}/${task.id}`}>
                        <TaskTypeComponent taskType={taskType} />
                    </Link>
                ),
            },
            {
                title: <FormattedMessage id="task.status" />,
                dataIndex: 'status',
                key: 'status',
                sorter: true,
                width: 150,
                align: 'center',
                render: (status: TaskStatusType, task: Task) => (
                    <Link to={`/tasks/${this.getPath(task)}/${task.id}`}>
                        <TaskStatusComponent status={status} />
                    </Link>
                ),
            },
        ];

        return (
            <Table
                dataSource={items}
                columns={columns}
                pagination={tableService.createPagination(tasksPage, { position: ['bottomRight'] })}
                rowKey="id"
                onChange={this.list}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading === 'loading'}
            />
        );
    };

    render() {
        return <LayoutComponent pageId="tasks">{this.renderContent()}</LayoutComponent>;
    }
}
export default injectIntl(TasksPage);

interface Props extends WrappedComponentProps {}

interface State {
    tasksPage?: Page<Task>;
    loading?: 'loading' | 'exporting';
    searchText?: string;
}
